"use client"

import type { CareerCenter } from "@/modules/careerCenter/types"
import type { JobAdsResult } from "@/modules/jobAds/client/api/types"
import type { AutocompleteSearchProperties } from "@/modules/jobAds/client/components/AutocompleteSearchInput/AutocompleteSearchInput"
import type { TrackingContext } from "@/modules/jobSearch/types/tracking"
import type { CurrentUserSearchConfig } from "@/modules/user/api/types"
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime"
import type Router from "next/dist/shared/lib/router/router"
import type { ParsedUrlQuery } from "querystring"

import { createContext, useContext } from "react"

export type PageContextValue = {
  /**
   * Search results data needed on client side
   */
  facets: JobAdsResult["facets"]
  /**
   * Career Center infos
   */
  careerCenterConf: CareerCenter["careerCenterConf"]
  careerCenterId: CareerCenter["id"] | null
  careerCenterLogo: CareerCenter["logo"] | null
  careerCenterMainId: Exclude<CareerCenter["mainCareerCenterId"], undefined> | null
  careerCenterShortName: CareerCenter["shortName"] | null
  /**
   * User infos
   */
  currentUserSearchConfig: CurrentUserSearchConfig | null
  isUserLogged: boolean
  /**
   * Feature flags
   */
  isDirectAlgoliaConnectionEnabled: boolean
  isSearchProxyV3ConnectionEnabled: boolean
  isJobAdsFoWoosmapMigrationEnabled: boolean
  /**
   * Callback to use when the keywork field is used
   */
  onSuggestionClicked?: (properties: AutocompleteSearchProperties) => void
  /**
   * Used to identify the params used in prefiltered searches.
   */
  prefilteredSearchParams: ParsedUrlQuery
  /**
   * The router push method to navigate through pages.
   * It is stored here to be able to use both App & Page router wherever the search is used.
   */
  push: AppRouterInstance["push"] | Router["push"]
  /**
   * Record to store the informations used to track the page events.
   */
  trackingContext: TrackingContext
}

export const PageContext = createContext<PageContextValue>({
  careerCenterConf: null,
  careerCenterId: null,
  careerCenterLogo: null,
  careerCenterMainId: null,
  careerCenterShortName: null,
  currentUserSearchConfig: null,
  facets: [],
  isDirectAlgoliaConnectionEnabled: false,
  isJobAdsFoWoosmapMigrationEnabled: false,
  isSearchProxyV3ConnectionEnabled: false,
  isUserLogged: false,
  onSuggestionClicked: () => {},
  prefilteredSearchParams: {},
  push: () => {},
  trackingContext: {} as TrackingContext,
})

export const usePageContext = (): PageContextValue => useContext(PageContext)
